import { InternetInterface } from '../ecdn11/interfaces';
import { IPv4, IPv6 } from 'ipaddr.js';

interface CandidateCarrier {
  iceCandidate: RTCIceCandidate;
  foundation: string;
  component: string;
  transport: string;
  address: string;
  priority: number;
  type: string;
}

function buildCarrier(candidate: RTCIceCandidate): CandidateCarrier {
  const candidateStr = candidate.candidate;
  const colonIdx = candidateStr.indexOf(':');
  const pieces = candidateStr.substring(colonIdx + 1).split(' ');

  const foundation = pieces[0];
  const component = pieces[1];
  const transport = pieces[2];
  const address = pieces[4];
  const priority = parseInt(pieces[5], 10);
  const type = pieces[7];
  return {
    iceCandidate: candidate,
    foundation,
    component,
    transport,
    address,
    priority,
    type
  };
}

function isHostCandidate(carrier: CandidateCarrier): boolean {
  return carrier.type === 'host';
}

// Parse the uint32 PRIORITY field into its constituent parts from RFC 5245,
// type preference, local preference, and (256 - component ID).
// ex: 126 | 32252 | 255 (126 is host preference, 255 is component ID 1)
function augmentPriority(priority: number, inc: number): number {
  // type priority >> 24, local priority >> 8,  component priority
  if (inc > 0) {
    // increase type and local pref by inc
    const typeAdd = inc >> 24;
    const localAdd = inc >> 8;
    return priority + typeAdd + localAdd;
  }

  return priority;
}

function generateFoundation(carrier: CandidateCarrier, inc: number): string {
  // https://tools.ietf.org/html/rfc5245#section-4.1.1.3
  // type
  // same base ip
  // same stun or turn server for reflexive or relay candidates
  // same transport
  // we only care about host. host candidates are their own base
  // prefix with k I guess
  return `k${inc}${carrier.foundation}`;
}

function generateCandidate(
  carrier: CandidateCarrier,
  newAddress: string,
  priorityInc: number
) {
  const candidate = carrier.iceCandidate;

  const candidateStr = candidate.candidate;
  const colonIdx = candidateStr.indexOf(':');
  const prefix = candidateStr.substring(0, colonIdx);
  const pieces = candidateStr.substring(colonIdx + 1).split(' ');

  const np = augmentPriority(carrier.priority, priorityInc);
  pieces[0] = generateFoundation(carrier, priorityInc);
  pieces[4] = newAddress;
  pieces[5] = String(np);

  return `${prefix}:${pieces.join(' ')}`;
}

function cloneCandidate(
  address: string,
  carrier: CandidateCarrier,
  priorityInc: number
): RTCIceCandidate {
  // TODO - modidify foundation.
  const candidate = carrier.iceCandidate;
  const cloned = new RTCIceCandidate({
    sdpMid: candidate.sdpMid,
    sdpMLineIndex: candidate.sdpMLineIndex,
    usernameFragment: candidate.usernameFragment,
    candidate: generateCandidate(carrier, address, priorityInc)
  });
  return cloned;
}

export default function processIceCandidates(
  candidates: RTCIceCandidate[],
  // 11 AgentData
  interfaces?: Record<string, InternetInterface>
): RTCIceCandidate[] {
  const ifcs = { ...interfaces };
  delete ifcs['connected'];

  if (!ifcs || Object.values(ifcs).length == 0) {
    return candidates;
  }

  const init = new Set<string>();
  const addressSet: Set<string> = Object.values(ifcs)
    .filter(i => i.status === 'on')
    .map(i => i.addresses)
    .reduce((acc: Set<string>, addresses: string[]) => {
      addresses
        .filter(a => IPv4.isValid(a) || IPv6.isValid(a))
        .forEach(a => acc.add(a));
      return acc;
    }, init);

  // remove all addresses with existing candidates
  const carriers = candidates.map(c => buildCarrier(c));
  carriers.forEach(c => {
    if (addressSet.has(c.address)) {
      addressSet.delete(c.address);
    }
  });

  // no addresses, then there's nothing to do
  if (addressSet.size == 0) {
    return candidates;
  }
  const addresses = Array.from(addressSet);
  return carriers.reduce((cs: RTCIceCandidate[], carrier: CandidateCarrier) => {
    // if the address is already in the set of addresses, we don't need to clone it.
    if (isHostCandidate(carrier) && !addressSet.has(carrier.address)) {
      // walk host candidates. and replace with addresses of type ipv4
      const toAdd = addresses.map((a, index) =>
        cloneCandidate(a, carrier, index + 1)
      );
      return [...cs, carrier.iceCandidate, ...toAdd];
    } else {
      return [...cs, carrier.iceCandidate];
    }
  }, []);
}
